/**
 * @file ProductForm.tsx
 *
 * The product create/edit/duplicate form — a faithful replica of the Website
 * Builder v2 ProductForm on this stack (react-intl, custom controlled-state
 * hooks, Gravity styling). Owns all editable state via {@link useProductFormState}
 * and {@link useProductFormMedia} plus local organisation/variant selections,
 * validates on save, and reports the assembled {@link ShopProductPayload} to its
 * parent. Cancelling with unsaved changes prompts for confirmation. Purely a
 * view+state component: fetching the seed product and persisting are the routed
 * screen's job.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type {
  ShopCategory,
  ShopProduct,
  ShopProductAttribute,
  ShopProductPayload,
  ShopProductVariant,
} from '../../api/shop-types';
import { ConfirmModal } from '../../components/ConfirmModal';
import { FormFooter } from '../../components/FormFooter';
import { MediaDropZone } from '../../components/MediaDropZone';
import {
  FormColumnLayout,
  FormSection,
  FormSectionHeading,
  FormSectionCard,
} from '../../components/form-layout/FormColumnLayout';
import { useProductFormState } from './use-product-form-state';
import { useProductFormMedia } from './use-product-form-media';
import {
  buildProductPayload,
  countCombinations,
  pruneVariantImages,
  relabelRowSelections,
  relabelSelectionLists,
  removedAxisValueLabels,
  validateVariantRows,
  variantsLostByAxisEdit,
  type VariantRowErrors,
} from './product-form-utils';
import {
  NEW_VARIANT_ID_PREFIX,
  isNewVariantRow,
  reclaimPersistedRows,
  suppressedCombinations,
  syncRowsToCombinations,
  variantComboKey,
} from './variant-matrix';
import {
  MAX_MEDIA_FILE_SIZE_MB,
  MAX_VARIANT_COMBINATIONS,
} from './product-form-constants';
import type { RelatedProductsMode } from './product-form-types';
import { ProductTypeSection } from './sections/ProductTypeSection';
import { ProductKindSection, type ProductKind } from './sections/ProductKindSection';
import { MediaUpload } from './sections/MediaUpload';
import { MediaLibraryModal } from '../../components/MediaLibraryModal';
import { DigitalProductFilePreview } from './sections/DigitalProductFilePreview';
import { ProductInformationSection } from './sections/ProductInformationSection';
import { ProductVariants } from './sections/ProductVariants';
import { VariantTable } from './sections/VariantTable';
import { PricingAndStockSection } from './sections/PricingAndStockSection';
import { ProductOrganisationSection } from './sections/ProductOrganisationSection';
import styles from './ProductForm.module.css';

/** Which flow the form is serving; drives the title and save label. */
export type ProductFormMode = 'create' | 'edit' | 'duplicate';

interface ProductFormProps {
  product?: ShopProduct;
  duplicate?: boolean;
  mode: ProductFormMode;
  categories: ShopCategory[];
  relatedCandidates: ShopProduct[];
  currencySymbol: string;
  saving: boolean;
  saveError?: string;
  /**
   * Reports the assembled payload and the edited per-variant rows. `variantRows` is empty
   * for a simple/digital product; for a variable product it carries the table's current
   * rows, from which the write path derives the batch-vs-aggregate dispatch.
   */
  onSubmit: (payload: ShopProductPayload, variantRows: ShopProductVariant[]) => void;
  onCancel: () => void;
}

/**
 * Renders the full product form.
 *
 * @param props - Seed product, mode, reference data, save state, and handlers.
 *
 * @returns The product form.
 */
export function ProductForm({
  product,
  duplicate = false,
  mode,
  categories,
  relatedCandidates,
  currencySymbol,
  saving,
  saveError,
  onSubmit,
  onCancel,
}: ProductFormProps) {
  const intl = useIntl();
  const { fields, patch, isDirty, errors, validate, firstErrorRef } =
    useProductFormState(product, duplicate);
  const media = useProductFormMedia(product);

  const [categoryIds, setCategoryIds] = useState<string[]>(
    product?.categories.map((c) => c.id) ?? [],
  );
  const [attributes, setAttributes] = useState<ShopProductAttribute[]>(
    product?.attributes ?? [],
  );
  const [crossSellIds, setCrossSellIds] = useState<string[]>(
    product?.cross_sell_ids ?? [],
  );
  const [relatedMode, setRelatedMode] = useState<RelatedProductsMode>(
    (product?.cross_sell_ids.length ?? 0) > 0 ? 'manual' : 'auto',
  );
  const [orgDirty, setOrgDirty] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [kind, setKind] = useState<ProductKind>(
    product && !product.virtual && (product.attributes.length ?? 0) > 0 ? 'variant' : 'simple',
  );
  const [variantRows, setVariantRows] = useState<ShopProductVariant[]>(
    product?.variants ?? [],
  );
  const variantBaselineRef = useRef<ShopProductVariant[]>(product?.variants ?? []);
  // Combinations that must never be previewed: expressible under the axes as loaded but
  // absent from the loaded rows — previously retired variants, which the read path
  // filters out. Kept as selector lists (relabelled with the axes on every edit) so the
  // client-side preview cannot offer to resurrect a deleted variant. Always empty for a
  // create/duplicate, where the engine mints the whole cartesian anyway.
  const suppressedCombosRef = useRef<{ name: string; value: string }[][]>([]);
  const [showDowngrade, setShowDowngrade] = useState(false);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [variantError, setVariantError] = useState<string | null>(null);
  const [variantRowErrors, setVariantRowErrors] = useState<Record<string, VariantRowErrors>>(
    {},
  );
  const [pendingAxes, setPendingAxes] = useState<{
    next: ShopProductAttribute[];
    lost: number;
    lostValues: string[];
  } | null>(null);

  // Re-seed organisation + variant state when the source product resolves after a fetch.
  useEffect(() => {
    setCategoryIds(product?.categories.map((c) => c.id) ?? []);
    setAttributes(product?.attributes ?? []);
    setCrossSellIds(product?.cross_sell_ids ?? []);
    setRelatedMode((product?.cross_sell_ids.length ?? 0) > 0 ? 'manual' : 'auto');
    setKind(
      product && !product.virtual && (product.attributes.length ?? 0) > 0
        ? 'variant'
        : 'simple',
    );
    // Seed the rows pre-pruned against the product's own gallery, and prune the BASELINE the
    // same way: the server enforces the invariant (migration 046 + write guard), so this is a
    // no-op for healthy data — but a stale reference (e.g. a soft-deleted asset still linked)
    // must not open an untouched form already dirty.
    const productGalleryIds = new Set((product?.images ?? []).map((img) => img.id));
    const prunedRows = pruneVariantImages(product?.variants ?? [], productGalleryIds);
    // A duplicate is a create: the source's variant ids are not ours to write to, so the
    // rows are re-keyed to synthetic combination ids (they ride the create as full
    // overrides) and start SKU-less — a SKU is unique across the shop, so the copy cannot
    // reuse the source's. Values (price/sale/stock/weight) carry over deliberately.
    const seededRows =
      mode === 'duplicate'
        ? prunedRows.map((row) => ({
            ...row,
            id: NEW_VARIANT_ID_PREFIX + variantComboKey(row.options),
            sku: '',
          }))
        : prunedRows;
    setVariantRows(seededRows);
    variantBaselineRef.current = seededRows;
    // Suppression applies only to an edit — a create/duplicate materialises the whole
    // cartesian on the engine anyway, so hiding a combination would just misrepresent it.
    suppressedCombosRef.current =
      product && mode === 'edit'
        ? suppressedCombinations(product.attributes, seededRows)
        : [];
    setOrgDirty(false);
    setTypeError(null);
    setVariantError(null);
    setVariantRowErrors({});
    setPendingAxes(null);
  }, [product, mode]);

  const isDigital = fields.productType === 'digital';
  const isNewProduct = mode !== 'edit';
  // Simple/Variable is a physical-only choice; digital is always a single variant.
  const isVariant = !isDigital && kind === 'variant';
  // The per-variant table is the live preview the options generate — it appears the
  // moment there is a combination to show, in create and edit alike (a not-yet-saved
  // combination is tagged "Unsaved" and rides the save as an override). A variant
  // product's price and stock live entirely per row, so the product-level pricing block
  // drops out for it (mirrors WSB); a simple/digital product keeps it.
  const showVariantTable = isVariant;
  const showPricingBlock = !isVariant;
  const combinationCount = countCombinations(attributes);

  const variantsDirty = useMemo(
    () => JSON.stringify(variantRows) !== JSON.stringify(variantBaselineRef.current),
    [variantRows],
  );
  const dirty = isDirty || media.isDirty || orgDirty || variantsDirty;

  const galleryImages = useMemo(
    () =>
      media.imageUrls.flatMap((url) => {
        const id = media.imageIdByUrl[url];
        return id ? [{ id, src: url }] : [];
      }),
    [media.imageUrls, media.imageIdByUrl],
  );

  // Mirror of the server's gallery cascade (and its 422 guard): removing a gallery image
  // clears it from any variant row that still used it, immediately and before the save.
  // Safe on load: the media state and the variant rows both seed synchronously from the
  // same `product` reference, so the two are never transiently inconsistent here.
  useEffect(() => {
    const validIds = new Set(galleryImages.map((image) => image.id));
    setVariantRows((prev) => pruneVariantImages(prev, validIds));
  }, [galleryImages]);

  // Switching to a digital product is refused while variants exist (no silent normalise).
  const handleTypeChange = (value: 'physical' | 'digital'): void => {
    if (value === 'digital' && attributes.length > 0) {
      setTypeError(intl.formatMessage(adminMessages.formDigitalHasVariantsError));
      return;
    }
    setTypeError(null);
    if (value === 'digital') {
      setKind('simple');
    }
    patch({ productType: value });
  };

  // Downgrading Variable → Simple discards every per-variant price/stock/SKU: confirm first.
  const handleKindChange = (value: ProductKind): void => {
    if (value === 'simple' && attributes.length > 0) {
      setShowDowngrade(true);
      return;
    }
    setKind(value);
  };

  const confirmDowngrade = (): void => {
    setAttributes([]);
    setVariantRows([]);
    setKind('simple');
    setOrgDirty(true);
    setShowDowngrade(false);
  };

  // Apply an axis edit: first follow in-place RENAMES (a value or axis whose id survives
  // with a new label — the engine renames it and keeps its variants, so the rows follow
  // instead of being pruned), then reconcile the table to the new cartesian: surviving
  // combinations keep their rows and edits, vanished ones drop out, and new ones appear
  // immediately as editable "Unsaved" rows seeded from the merchant's existing work.
  // A combination that left and re-entered the matrix (an axis added and removed again)
  // is handed back its persisted identity, so its edits stay saveable by id.
  const applyAxes = (next: ShopProductAttribute[]): void => {
    const suppressed = relabelSelectionLists(attributes, next, suppressedCombosRef.current);
    suppressedCombosRef.current = suppressed;
    setAttributes(next);
    setVariantRows((prev) => {
      const relabeled = relabelRowSelections(attributes, next, prev);
      return reclaimPersistedRows(
        syncRowsToCombinations(next, relabeled, suppressed),
        variantBaselineRef.current,
      );
    });
    setOrgDirty(true);
  };

  // Guard an axis edit that would retire materialized variants (a genuinely REMOVED value —
  // renames follow their id and are never a loss) behind a confirm naming the lost values;
  // a purely additive edit or a pure rename applies straight away. `confirmed` skips the
  // guard for a gesture that already carried its own warning (the whole-option delete
  // modal) — one warning per gesture, never two.
  const handleAxesChange = (
    next: ShopProductAttribute[],
    meta?: { confirmed?: boolean },
  ): void => {
    // Only MATERIALISED variants count as a loss — a not-yet-saved preview row has no
    // price/stock/SKU on the engine to destroy, so dropping one warns about nothing.
    const persistedRows = variantRows.filter((row) => !isNewVariantRow(row));
    const lost = variantsLostByAxisEdit(
      next,
      relabelRowSelections(attributes, next, persistedRows),
    );
    if (!meta?.confirmed && lost > 0) {
      setPendingAxes({ next, lost, lostValues: removedAxisValueLabels(attributes, next) });
      return;
    }
    applyAxes(next);
  };

  const handleSave = (): void => {
    setVariantError(null);
    setVariantRowErrors({});
    // A valid digital product is one variant with no options — refuse to save one with axes
    // (the type switch already blocks reaching this state, but never silently normalise).
    if (isDigital && attributes.length > 0) {
      setTypeError(intl.formatMessage(adminMessages.formDigitalHasVariantsError));
      return;
    }
    if (isVariant && combinationCount > MAX_VARIANT_COMBINATIONS) {
      setVariantError(
        intl.formatMessage(adminMessages.formVariantTooMany, {
          count: combinationCount,
          max: MAX_VARIANT_COMBINATIONS,
        }),
      );
      return;
    }
    // Every enabled variant row must carry a valid price/sale/stock (and a unique SKU)
    // before the save round-trip: block submit, mark the offending rows inline, and
    // surface the standard notification. Covers create too — with the pricing block gone
    // for a variant product, each row's own price is the only price there is.
    if (isVariant) {
      const rowErrors = validateVariantRows(variantRows);
      if (Object.keys(rowErrors).length > 0) {
        setVariantRowErrors(rowErrors);
        setVariantError(intl.formatMessage(adminMessages.formVariantRowErrors));
        return;
      }
    }
    if (!validate()) {
      return;
    }
    const payload = buildProductPayload({
      fields,
      imageUrls: media.imageUrls,
      imageIdByUrl: media.imageIdByUrl,
      downloads: media.downloads,
      categoryIds,
      attributes,
      crossSellIds: relatedMode === 'manual' ? crossSellIds : [],
    });
    // A variant create/duplicate has no product-level price field (the pricing block is
    // per row), but the engine's create still requires one base price to seed the matrix
    // from — derive it from the first sellable row; every row also rides as a full
    // override, so the base only satisfies the required field.
    if (isVariant && isNewProduct) {
      const firstSellable = variantRows.find((row) => row.enabled);
      if (firstSellable) {
        payload.regular_price = firstSellable.regular_price;
      }
    }
    // A variant product always hands the table's rows to the write path: on an edit they
    // are diffed against the baseline (batch or structural overrides); on a create or
    // duplicate every row rides as a full override so what the merchant saw is what is
    // materialised (a duplicate's rows are seeded from the source, SKU-less).
    onSubmit(payload, isVariant ? variantRows : []);
  };

  const handleCancel = (): void => {
    if (dirty) {
      setShowUnsaved(true);
    } else {
      onCancel();
    }
  };

  // WSB-v2 titles the edit screen with the product's own name, not "Edit product".
  const headingTitle =
    mode === 'edit'
      ? (product?.name ?? intl.formatMessage(adminMessages.formEditTitle))
      : mode === 'duplicate'
        ? intl.formatMessage(adminMessages.formDuplicateTitle)
        : intl.formatMessage(adminMessages.formAddTitle);
  const saveLabel = mode === 'edit' ? adminMessages.formSave : adminMessages.formCreate;

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/products">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">{headingTitle}</h2>
      </header>

      <FormColumnLayout>
        <FormSection>
          <FormSectionHeading title={intl.formatMessage(adminMessages.formProductType)} />
          <FormSectionCard>
            <ProductTypeSection value={fields.productType} onChange={handleTypeChange} />
            {typeError && (
              <p
                className="gv-text-sm gv-state-critical"
                role="alert"
                data-testid="product-form-type-error"
              >
                {typeError}
              </p>
            )}
          </FormSectionCard>
        </FormSection>

        <FormSection>
          <FormSectionHeading title={intl.formatMessage(adminMessages.formInformation)} />
          <FormSectionCard>
            <ProductInformationSection
              name={fields.name}
              onNameChange={(value) => patch({ name: value })}
              nameError={errors.nameError}
              nameRef={firstErrorRef}
              status={fields.status}
              onStatusChange={(value) => patch({ status: value })}
              description={fields.description}
              onDescriptionChange={(value) => patch({ description: value })}
              shortDescription={fields.shortDescription}
              onShortDescriptionChange={(value) => patch({ shortDescription: value })}
            />
          </FormSectionCard>
        </FormSection>

        <FormSection>
          <FormSectionHeading
            title={intl.formatMessage(adminMessages.formMedia)}
            showOptional
          />
          <FormSectionCard>
            <MediaUpload
              imageUrls={media.imageUrls}
              onImagesChange={media.onImagesChange}
              onImagesSelected={media.onImagesSelected}
              uploadingCount={media.uploadingCount}
              uploadErrors={media.uploadErrors}
              onSelectFromLibrary={() => setLibraryOpen(true)}
            />
          </FormSectionCard>
        </FormSection>

        {isDigital && (
          <FormSection>
            <FormSectionHeading
              title={intl.formatMessage(adminMessages.formDownloadableFiles)}
            />
            <FormSectionCard>
              <MediaDropZone
                acceptAllTypes
                testId="product-form-files"
                onFilesSelected={media.onDownloadsSelected}
                maxFileSizeMb={MAX_MEDIA_FILE_SIZE_MB}
                remainingSlots={Number.MAX_SAFE_INTEGER}
                title={intl.formatMessage(adminMessages.formMediaDropTitle)}
                browseLabel={intl.formatMessage(adminMessages.formUploadFiles)}
                hint={intl.formatMessage(adminMessages.formDownloadableHint, {
                  maxMb: MAX_MEDIA_FILE_SIZE_MB,
                })}
              />
              <DigitalProductFilePreview
                files={media.downloads}
                onFilesChange={media.onDownloadsChange}
                uploadingCount={media.downloadUploadingCount}
              />
            </FormSectionCard>
          </FormSection>
        )}

        {!isDigital && (
          <FormSection>
            <FormSectionHeading title={intl.formatMessage(adminMessages.formConfiguration)} />
            <FormSectionCard>
              <ProductKindSection value={kind} onChange={handleKindChange} />
            </FormSectionCard>
          </FormSection>
        )}

        {/* The variants section reads options → generated rows top to bottom (the WSB
            arrangement): the axes editor, then the live per-variant grid the options
            generate, with the save-blocking error between them. */}
        {showVariantTable && (
          <FormSection>
            <FormSectionHeading title={intl.formatMessage(adminMessages.formVariantTableTitle)} />
            <FormSectionCard>
              <ProductVariants attributes={attributes} onChange={handleAxesChange} />
              {variantError && (
                <p
                  className="gv-text-sm gv-state-critical"
                  role="alert"
                  data-testid="product-form-variant-error"
                >
                  {variantError}
                </p>
              )}
              <VariantTable
                variants={variantRows}
                onChange={setVariantRows}
                currencySymbol={currencySymbol}
                galleryImages={galleryImages}
                combinationCount={combinationCount}
                maxCombinations={MAX_VARIANT_COMBINATIONS}
                errors={variantRowErrors}
              />
            </FormSectionCard>
          </FormSection>
        )}

        {showPricingBlock && (
          <FormSection>
            <FormSectionHeading
              title={intl.formatMessage(adminMessages.formPricingAndStock)}
            />
            <FormSectionCard>
              <PricingAndStockSection
                fields={fields}
                errors={errors}
                currencySymbol={currencySymbol}
                onChange={patch}
              />
              {variantError && (
                <p
                  className="gv-text-sm gv-state-critical"
                  role="alert"
                  data-testid="product-form-variant-error"
                >
                  {variantError}
                </p>
              )}
            </FormSectionCard>
          </FormSection>
        )}

        <FormSection>
          <FormSectionHeading title={intl.formatMessage(adminMessages.formOrganisation)} />
          <FormSectionCard>
            <ProductOrganisationSection
              categories={categories}
              selectedCategoryIds={categoryIds}
              onCategoryIdsChange={(ids) => {
                setCategoryIds(ids);
                setOrgDirty(true);
              }}
              relatedCandidates={relatedCandidates}
              relatedMode={relatedMode}
              onRelatedModeChange={(value) => {
                setRelatedMode(value);
                setOrgDirty(true);
              }}
              selectedRelatedIds={crossSellIds}
              onRelatedIdsChange={(ids) => {
                setCrossSellIds(ids);
                setOrgDirty(true);
              }}
            />
          </FormSectionCard>
        </FormSection>
        {saveError && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="product-form-error"
          >
            {saveError}
          </p>
        )}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-cancel"
            data-testid="product-form-cancel"
            onClick={handleCancel}
            disabled={saving}
          >
            <FormattedMessage {...adminMessages.formCancel} />
          </button>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="product-form-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.formSaving} />
            ) : (
              <FormattedMessage {...saveLabel} />
            )}
          </button>
        </FormFooter>
      </FormColumnLayout>

      {showUnsaved && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.formUnsavedTitle)}
          message={intl.formatMessage(adminMessages.formUnsavedMessage)}
          confirmLabel={intl.formatMessage(adminMessages.formUnsavedConfirm)}
          cancelLabel={intl.formatMessage(adminMessages.formUnsavedCancel)}
          loadingLabel={intl.formatMessage(adminMessages.formUnsavedConfirm)}
          confirmVariant="primary"
          onPrimaryClick={onCancel}
          onSecondaryClick={() => setShowUnsaved(false)}
          onClose={() => setShowUnsaved(false)}
        />
      )}

      {pendingAxes && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.formVariantValueLossTitle)}
          message={intl.formatMessage(adminMessages.formVariantValueLossMessage, {
            values: pendingAxes.lostValues.join(', '),
            count: pendingAxes.lost,
          })}
          confirmLabel={intl.formatMessage(adminMessages.formVariantValueLossConfirm)}
          cancelLabel={intl.formatMessage(adminMessages.formCancel)}
          loadingLabel={intl.formatMessage(adminMessages.formVariantValueLossConfirm)}
          confirmVariant="destructive"
          onPrimaryClick={() => {
            applyAxes(pendingAxes.next);
            setPendingAxes(null);
          }}
          onSecondaryClick={() => setPendingAxes(null)}
          onClose={() => setPendingAxes(null)}
        />
      )}

      {showDowngrade && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.formVariantDowngradeTitle)}
          message={intl.formatMessage(adminMessages.formVariantDowngradeMessage, {
            count: variantRows.filter((v) => v.enabled).length || combinationCount,
          })}
          confirmLabel={intl.formatMessage(adminMessages.formVariantDowngradeConfirm)}
          cancelLabel={intl.formatMessage(adminMessages.formCancel)}
          loadingLabel={intl.formatMessage(adminMessages.formVariantDowngradeConfirm)}
          confirmVariant="destructive"
          onPrimaryClick={confirmDowngrade}
          onSecondaryClick={() => setShowDowngrade(false)}
          onClose={() => setShowDowngrade(false)}
        />
      )}

      <MediaLibraryModal
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onAdd={(items) =>
          media.addLibraryImages(
            items
              .filter((item) => item.mediaType === 'image')
              .map((item) => ({ url: item.url, id: String(item.id) })),
          )
        }
      />
    </div>
  );
}
